// libraries.
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// store.
import { RootState } from "@store";
import {
  setIsSaving,
  updateException,
  updateIncentiveStep,
  updateRawRule,
  addRawRule,
} from "./rulesSlice";

// endpoints.
import { useUpdateRuleMutation } from "@store/apiSlices/incentiveRules/editRules.endpoints";
import { useToggleRuleMutation } from "@store/apiSlices/incentiveRules/toggleRules.endpoints";
import { useCreateRuleMutation } from "@store/apiSlices/incentiveRules/createRule.endpoints";

// interfaces.
import { ExceptionRule, IncentiveStep } from "@pages/RulesPage/modules/RulesMain/rulesData";
import {
  RawRule,
  RawRuleItem,
  RulesSchema,
  CreateRuleBody,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

type IsActive = "active" | "inactive";

const TOAST_IDS = {
  exception: "saveExceptionLoading",
  step: "saveStepLoading",
  toggle: "toggleRuleLoading",
};

export const useRulesSlice = () => {
  const dispatch = useDispatch();
  const [updateRuleMutation] = useUpdateRuleMutation();
  const [toggleRuleMutation] = useToggleRuleMutation();
  const [createRuleMutation] = useCreateRuleMutation();

  const exceptions = useSelector(
    (state: RootState) => state.rulesReducer.exceptions,
  );
  const incentiveStepsCO = useSelector(
    (state: RootState) => state.rulesReducer.incentiveStepsCO,
  );
  const incentiveStepsMX = useSelector(
    (state: RootState) => state.rulesReducer.incentiveStepsMX,
  );
  const rawRulesCO = useSelector(
    (state: RootState) => state.rulesReducer.rawRulesCO,
  );
  const rawRulesMX = useSelector(
    (state: RootState) => state.rulesReducer.rawRulesMX,
  );
  const isSaving = useSelector(
    (state: RootState) => state.rulesReducer.isSaving,
  );

  const getRawRule = useCallback(
    (ruleId: string, country: string): RawRuleItem | undefined => {
      const list = country === "MX" ? rawRulesMX : rawRulesCO;
      return list.find((r) => r.rule_id === ruleId);
    },
    [rawRulesCO, rawRulesMX],
  );

  const saveException = useCallback(
    async (
      updated: ExceptionRule,
      updatedRule: RawRule,
      country: string,
      ruleId: string,
    ) => {
      const schema: RulesSchema =
        country === "MX" ? "inventory_mx" : "inventory_co";
      dispatch(setIsSaving(true));
      toast.loading("Guardando excepción...", {
        autoClose: false,
        toastId: TOAST_IDS.exception,
      });
      try {
        const result = await updateRuleMutation({
          rule_id: ruleId,
          schema,
          rule: updatedRule,
        }).unwrap();
        dispatch(updateException(updated));
        dispatch(updateRawRule({ country, rawRule: result }));
        toast.dismiss(TOAST_IDS.exception);
        toast.success("Excepción actualizada correctamente");
      } catch {
        toast.dismiss(TOAST_IDS.exception);
        toast.error("Error al guardar la excepción");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch, updateRuleMutation],
  );

  const saveIncentiveStep = useCallback(
    async (
      country: string,
      updated: IncentiveStep,
      updatedRule: RawRule,
      ruleId: string,
    ) => {
      const schema: RulesSchema =
        country === "MX" ? "inventory_mx" : "inventory_co";
      dispatch(setIsSaving(true));
      toast.loading("Guardando paso de incentivo...", {
        autoClose: false,
        toastId: TOAST_IDS.step,
      });
      try {
        const result = await updateRuleMutation({
          rule_id: ruleId,
          schema,
          rule: updatedRule,
        }).unwrap();
        dispatch(updateIncentiveStep({ country, step: updated }));
        dispatch(updateRawRule({ country, rawRule: result }));
        toast.dismiss(TOAST_IDS.step);
        toast.success("Paso actualizado correctamente");
      } catch {
        toast.dismiss(TOAST_IDS.step);
        toast.error("Error al guardar el paso");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch, updateRuleMutation],
  );

  const toggleRule = useCallback(
    async (rawRule: RawRuleItem, country: string, changedBy = "") => {
      const schema: RulesSchema = country === "MX" ? "inventory_mx" : "inventory_co";
      const newStatus: IsActive = rawRule.is_active === "active" ? "inactive" : "active";
      const label = newStatus === "active" ? "activada" : "desactivada";

      dispatch(setIsSaving(true));
      toast.loading("Actualizando estado...", {
        autoClose: false,
        toastId: TOAST_IDS.toggle,
      });
      try {
        await toggleRuleMutation({
          id: rawRule.id,
          rule_id: rawRule.rule_id,
          schema,
          is_active: newStatus,
          changed_by: changedBy,
        }).unwrap();
        dispatch(updateRawRule({ country, rawRule: { ...rawRule, is_active: newStatus } }));
        toast.dismiss(TOAST_IDS.toggle);
        toast.success(`Regla ${label} correctamente`);
      } catch {
        toast.dismiss(TOAST_IDS.toggle);
        toast.error("Error al actualizar el estado de la regla");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch, toggleRuleMutation],
  );

  const createRule = useCallback(
    async (body: CreateRuleBody) => {
      dispatch(setIsSaving(true));
      toast.loading("Creando regla...", { autoClose: false, toastId: "createRuleLoading" });
      try {
        const result = await createRuleMutation(body).unwrap();
        dispatch(addRawRule({ country: body.schema === "inventory_mx" ? "MX" : "CO", rawRule: result }));
        toast.dismiss("createRuleLoading");
        toast.success("Regla creada correctamente");
      } catch {
        toast.dismiss("createRuleLoading");
        toast.error("Error al crear la regla");
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [dispatch, createRuleMutation],
  );

  return {
    exceptions,
    incentiveStepsCO,
    incentiveStepsMX,
    rawRulesCO,
    rawRulesMX,
    isSaving,
    getRawRule,
    saveException,
    saveIncentiveStep,
    toggleRule,
    createRule,
  };
};
